import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileUp, Loader2, CheckCircle2, AlertTriangle, Users } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const HEADERS = new Set(["email", "e-mail", "emails", "email address", "address"]);

function parseEmails(text) {
  const tokens = (text || "").split(/[\s,;]+/).map((t) => t.trim().toLowerCase()).filter(Boolean);
  const valid = [], invalid = [], seen = new Set();
  for (const t of tokens) {
    if (HEADERS.has(t)) continue;
    if (!EMAIL_RE.test(t)) { invalid.push(t); continue; }
    if (seen.has(t)) continue;
    seen.add(t); valid.push(t);
  }
  return { valid, invalid };
}

// Reusable "Import CSV / paste emails" dialog. Posts {emails} to `endpoint`.
export function TeamCsvImport({ endpoint, teamName, seatsLeft, onImported, testid = "csv" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const parsed = parseEmails(text);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const content = await f.text();
    setText((prev) => (prev ? prev + "\n" : "") + content);
    e.target.value = "";
  };

  const doImport = async () => {
    if (!parsed.valid.length) { toast.error("No valid email addresses found."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { emails: parsed.valid });
      setResult(data.summary);
      const s = data.summary;
      toast.success(
        `Imported ${s.total_added} member${s.total_added === 1 ? "" : "s"}` +
        (s.created.length ? ` (${s.created.length} new account${s.created.length === 1 ? "" : "s"})` : "") + "."
      );
      onImported?.(data.team);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed.");
    } finally { setBusy(false); }
  };

  const reset = () => { setText(""); setResult(null); };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { reset(); setOpen(true); }}
        data-testid={`${testid}-import-btn`}
        className="text-xs bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
        <FileUp className="w-3.5 h-3.5 mr-1" /> Import CSV
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-import-dialog`} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import members{teamName ? ` — ${teamName}` : ""}</DialogTitle>
            <DialogDescription>
              Upload a .csv or paste email addresses (one per line or comma-separated). Emails without
              an account are registered automatically and emailed an invite to set their password.{" "}
              {seatsLeft != null && <>Seats available: <b>{seatsLeft}</b>.</>}
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-3">
              <input ref={fileRef} type="file" accept=".csv,.txt" onChange={onFile}
                className="hidden" data-testid={`${testid}-file-input`} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                data-testid={`${testid}-choose-file`} className="w-full">
                <Upload className="w-4 h-4 mr-1.5" /> Choose CSV file
              </Button>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={7}
                placeholder={"student1@email.com\nstudent2@email.com\nstudent3@email.com"}
                data-testid={`${testid}-textarea`} className="font-mono text-sm" />
              <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid={`${testid}-preview`}>
                <Users className="w-3.5 h-3.5" />
                <span><b className="text-slate-700">{parsed.valid.length}</b> valid email{parsed.valid.length === 1 ? "" : "s"} detected
                {parsed.invalid.length ? ` · ${parsed.invalid.length} invalid skipped` : ""}.</span>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5 text-sm" data-testid={`${testid}-result`}>
              <ResultLine ok label="Added to team" n={result.total_added} testid={`${testid}-res-added`} />
              <ResultLine label="New accounts created" n={result.created.length} />
              <ResultLine label="Already on this team" n={result.already_members.length} />
              <ResultLine warn label="Skipped — no seats left" n={result.skipped_no_seats.length} testid={`${testid}-res-seats`} />
              <ResultLine warn label="On another team (skipped)" n={result.on_other_team.length} />
              <ResultLine warn label="Invalid addresses" n={result.invalid.length} />
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <Button onClick={doImport} disabled={busy || !parsed.valid.length}
                data-testid={`${testid}-do-import`} className="bg-[#0047AB] hover:bg-[#003380] text-white">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                Import {parsed.valid.length || ""} member{parsed.valid.length === 1 ? "" : "s"}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={reset} data-testid={`${testid}-import-more`}>Import more</Button>
                <Button onClick={() => setOpen(false)} data-testid={`${testid}-import-done`}
                  className="bg-[#0047AB] hover:bg-[#003380] text-white">Done</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResultLine({ label, n, ok, warn, testid }) {
  if (!n) return null;
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : Users;
  const color = ok ? "text-emerald-600" : warn ? "text-amber-600" : "text-slate-600";
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="flex items-center gap-1.5 text-slate-600"><Icon className={`w-3.5 h-3.5 ${color}`} /> {label}</span>
      <span className={`font-bold ${color}`}>{n}</span>
    </div>
  );
}
